import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function PnlDistributionWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "range", label: t.analytics.distributionLabel, align: "left" as const },
    { key: "count", label: t.analytics.numberOfTrades, align: "right" as const },
  ];
  const tableRows = (analyticsData?.pnlDistribution ?? []).map((d) => ({
    range: d.range,
    count: d.count,
  }));

  const chartOptions = useMemo(() => {
    if (!analyticsData) return null;
    const { pnlDistribution } = analyticsData;
    const c = chartColors();
    return {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
        animations: { enabled: false },
      },
      plotOptions: {
        bar: { borderRadius: 3, columnWidth: "70%", distributed: true },
      },
      xaxis: {
        categories: pnlDistribution.map((d) => d.range),
        labels: {
          style: { colors: c.axis, fontSize: "10px" },
          rotate: -35,
          rotateAlways: false,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: t.analytics.numberOfTrades,
          style: { color: c.axis, fontSize: "11px" },
        },
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: { formatter: (val: number) => `${val} trades` },
      },
      // distributed:true treats each bar as its own series, so ApexCharts needs
      // one color per bar — a single-element array falls back to its default
      // (off-theme green) palette. Repeat the accent across every category.
      colors: pnlDistribution.map(() => c.accent),
      legend: { show: false },
      dataLabels: { enabled: false },
    };
  }, [analyticsData, isDark, themeId, t.analytics.numberOfTrades]);

  if (!analyticsData || analyticsData.pnlDistribution.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.distributionLabel}
        isDark={isDark}
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="px-5 pb-5 pt-1 flex-1 min-h-0">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : chartOptions ? (
          <LazyApexChart
            key={`pnl-dist-widget-${themeId}`}
            options={chartOptions}
            series={[
              {
                name: t.analytics.numberOfTrades,
                data: analyticsData.pnlDistribution.map((d) => d.count),
              },
            ]}
            type="bar"
            height="100%"
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
